<script setup>
  import { computed } from "vue";
  import { storeToRefs } from "pinia";
  import { Loader2 } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import AiStar from "@/components/episto-ui/ai/AiStar.vue";
  import { Banner } from "@/components/episto-ui/banner";
  import { useSurveyProfilesStore } from "@stores/survey/surveyProfiles";

  defineProps({ surveyToken: { type: String, required: true } });

  const SHORTCUTS = [
    { label: "The most relevant paths", kinds: undefined },
    { label: "One profile per target", kinds: ["target"] },
    { label: "One profile per screen-out", kinds: ["screenout"] },
  ];

  const surveyProfilesStore = useSurveyProfilesStore();
  const { surveyProfiles, checking, locked, launchErrorMessage } = storeToRefs(surveyProfilesStore);

  const running = computed(() => surveyProfiles.value.filter((profile) => profile.ongoing_creation));
  const status = computed(() => {
    if (checking.value) return "Launching the fake respondents…";
    const done = surveyProfiles.value.length - running.value.length;
    return `Fake respondents are walking the questionnaire · ${done} of ${surveyProfiles.value.length} profiles done`;
  });
</script>

<template>
  <Banner
    v-if="launchErrorMessage"
    variant="error"
    :message="launchErrorMessage" />
  <section
    v-if="locked"
    class="flex items-center gap-2.5 rounded-xl border bg-muted px-4 py-2.5">
    <Loader2 class="size-4 animate-spin text-primary" />
    <span class="font-medium text-primary">{{ status }}</span>
    <span class="text-muted-foreground">· the profiles are locked until it ends</span>
    <Button
      v-if="!checking"
      variant="outline"
      size="sm"
      class="ml-auto"
      @click="surveyProfilesStore.cancelRun(surveyToken)">
      Cancel
    </Button>
  </section>
  <section
    v-else
    class="flex flex-wrap items-center gap-2 rounded-xl border bg-muted px-4 py-3">
    <AiStar class="size-4 text-primary" />
    <span class="font-semibold text-primary">Let Episto AI build the test profiles</span>
    <span class="text-muted-foreground">from</span>
    <Button
      v-for="shortcut in SHORTCUTS"
      :key="shortcut.label"
      variant="outline"
      size="sm"
      class="h-7 rounded-full bg-background text-xs"
      @click="surveyProfilesStore.checkProgramming(surveyToken, shortcut.kinds)">
      {{ shortcut.label }}
    </Button>
  </section>
</template>
